/**
 * useReannouncingState — outcome state for a polite live region that must be
 * announced AGAIN when the same outcome repeats (055, WCAG 4.1.3).
 *
 * Setting an identical value is a no-op React update, so the region's text
 * would not change and a screen reader would hear nothing on the second
 * refusal. `announce` clears to `idle` first and re-sets the outcome after a
 * short delay. The two mutations land in separate tasks: several AT/browser
 * pairs merge a remove + re-insert of the same text within one task and
 * announce nothing. Any other update during the delay wins over the re-set.
 */

import { type SetStateAction, useEffect, useRef, useState } from 'react';

export const REANNOUNCE_DELAY_MS = 100;

export function useReannouncingState<S>(
  idle: S,
): readonly [state: S, setState: (next: SetStateAction<S>) => void, announce: (next: S) => void] {
  const [state, setRawState] = useState<S>(idle);
  // Bumped on every write so a pending re-set can tell it was superseded.
  const generationRef = useRef(0);
  const stateRef = useRef(state);
  stateRef.current = state;
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const setState = (next: SetStateAction<S>): void => {
    generationRef.current += 1;
    clearTimeout(timerRef.current);
    setRawState(next);
  };

  const announce = (next: S): void => {
    if (next === idle || stateRef.current !== next) {
      setState(next);
      return;
    }
    setState(idle);
    const generation = generationRef.current;
    timerRef.current = setTimeout(() => {
      if (generationRef.current === generation) setState(next);
    }, REANNOUNCE_DELAY_MS);
  };

  return [state, setState, announce];
}
